// safe new design
import styled, {css, DefaultTheme, FlattenInterpolation, ThemeProps} from 'styled-components';
import {useSelector} from 'react-redux';

import {isJustEat} from '~/shared/config';
import {selectUserData} from '~/shared/store/selectors';
import {Link} from '~/shared/router';
import useOpenSignIn from '~/shared/hooks/useOpenSignIn';
import {getLocalizationService} from '~/shared/services/localisationService';
import LanguageSelector from '~/shared/components/header/LanguageSelector';
import {useIsMinLargeMobile} from '~/shared/hooks/deviceInfo';
import {media} from '~/shared/theme/media';
import {pieButtonGhostSmallStyles} from '~/shared/theme/pie/PieButton';

import UserMenu from './UserMenu';

type Variant = 'guest' | 'defaultUser' | 'momentsUser';

const guestStyles = css`
  gap: 8px;
  ${media.minLargeMobile`
    gap: 24px;
  `}
`;

const defaultUserStyles = css`
  gap: 16px;
  ${media.minLargeMobile`
    gap: 24px;
  `}
  ${media.minLargeTablet`
    gap: 48px;
  `}
  ${media.minLargeDesktop`
    gap: 60px;
  `}
`;

const momentsUserStyles = css`
  gap: 16px;
  ${media.minLargeMobile`
    gap: 24px;
  `}
  ${media.minLargeTablet`
    gap: 32px;
  `}
  ${media.minLargeDesktop`
    gap: 60px;
  `}
`;

const variantsMap: Record<Variant, FlattenInterpolation<ThemeProps<DefaultTheme>>> = {
  guest: guestStyles,
  defaultUser: defaultUserStyles,
  momentsUser: momentsUserStyles,
};

const Wrapper = styled.div<{reverse: boolean; variant: Variant}>`
  display: flex;
  align-items: center;
  justify-content: center;
  ${({reverse}) => reverse && 'flex-direction: row-reverse;'}
  ${({variant}) => variantsMap[variant]}
`;

const LoginLink = styled(Link)`
  ${pieButtonGhostSmallStyles}
`;

const LanguageAndUserMenu = ({isMomentsUser}: {
  isMomentsUser: boolean;
}) => {
  const {t} = getLocalizationService();
  
  const {openLogin, openSignUp} = useOpenSignIn();
  
  const isUserLoggedIn = Boolean(useSelector(selectUserData));
  const isMinLargeMobile = useIsMinLargeMobile();

  return (
    <Wrapper
      reverse={isMinLargeMobile || !isUserLoggedIn}
      variant={!isUserLoggedIn ?
        'guest' :
        isMomentsUser ?
          'momentsUser' :
          'defaultUser'}
    >
      <div>
        <LanguageSelector variant={isJustEat ? 'ghost' : 'normal'} />
      </div>
      <div>
        {isUserLoggedIn ? (
          <UserMenu isMinLargeMobile={isMinLargeMobile} />
        ) : (
          <LoginLink data-test-id="loginBtn" onClick={isJustEat ? openSignUp : openLogin}>{t('login')}</LoginLink>
        )}
      </div>
    </Wrapper>
  );
};

export default LanguageAndUserMenu;
